/**
 * Landing page copy.
 *
 * Every user-facing marketing string lives in src/content/ so that (a) the
 * banned-words gate can scan one folder and (b) a lawyer can review one folder.
 *
 * Voice, per KEV_Design_System §06 and kev-language-and-guardrails §1:
 *   Precise, dry-witted, accountable. Never pushy. No hype, no emoji, no "locks."
 *   Short sentences. Plain English. Grade 8 reading level.
 *
 * Rewritten 2026-07-31 (Ned) to strip the tells of machine-written marketing:
 *   - No binary contrasts ("Most tips do X. KEV is the opposite.")
 *   - No negative triples ("No reasoning, no record, no way to tell.")
 *   - No pull-quote endings. A paragraph can just stop.
 *   - Say what the product does, not what it isn't. Optimistic and generic
 *     where we'd previously have listed a gap.
 *   - No data-provider names on marketing surfaces. They live in Terms §7 and
 *     the Privacy Policy, where a reader is actually asking who holds what.
 *
 * Hard constraints, unchanged:
 *   §1.3  banned words — enforced by content.test.ts
 *   §1.3  no win-rate or ROI claim until the public ledger exists
 *   §2.3  no stake sizing, ever
 *   §7    never invent a market price, line, or implied probability
 */

export const HERO = {
  headline: "Your Sharpest Mate",
  tagline: "Knowledge. Edge. Value.",
  /* "The analyst at the end of the bar" is the brand's own line (§06 / site). */
  sub: "The analyst at the end of the bar.",
  ctaPrimary: "See the models",
  ctaSecondary: "How it works",
} as const;

export const IDEA = {
  eyebrow: "The Idea",
  heading: "A number, with the working",
  body: [
    "Ask Kev about a game and you get a number with a one-line reason. Ask for more and you get the form, the sample and the match-up behind it, down to which seasons and games.",
  ],
} as const;

/* The `register` lines ("Dry, clinical, mildly smug when right.") came out on
   2026-07-31 (Ned): describing a model's demeanour to someone who hasn't spoken
   to it yet is a claim they can't check, and it pushed the useful sentence, what
   each one actually answers, down the card. */
export const PERSONAS = {
  eyebrow: "The Brains Of The Operation",
  heading: "Kev & Franco",
  intro: "Two models, same rulebook. Pick the one that matches your question.",
  kev: {
    name: "Kev",
    mode: "Sport",
    body: "Player props, team totals and match-ups. Kev gives you the read and the uncertainty sitting around it.",
  },
  franco: {
    name: "Franco",
    mode: "Fantasy",
    /* "Franco runs hotter and works to the same rules" came off on 2026-08-27
       (Ned). "Runs hotter" is a temperament claim that reads as a performance
       one on a card next to Kev's — it invites "hotter meaning better?", which
       is a question about results, and §1.3 is exactly about not answering that
       in marketing copy. The second half was redundant anyway: PERSONAS.intro
       two lines up already says "Two models, same rulebook". */
    body: "Trades, captaincy, and who steps up when someone sits.",
  },
  /* Sends the reader to /team, which is where each model's intended purpose is
     stated at length. The cards separate the two; they don't say what either is
     for, and that question is the one a reader has next. */
  more: "Meet the team",
} as const;

/* WHAT_KEV_DOES ("What you can hold us to") was cut on 2026-10-05 (Ned's
   site cut list): it restated The Idea a section later. */

/* The ninth cell of the model grid, and the only one that is not a model.

   THE GRID IS 3 ACROSS AND HELD EIGHT MODELS, so football's arrival left a hole
   in the bottom-right corner. ModelGrid's own note says why that matters: "a
   grid with holes in it reads as a bug; 'Not planned', said out loud, reads as
   accountable". A blank cell is neither — it says nothing, and looks like the
   page failed to finish drawing.

   It deliberately carries NO badge and no "Not yet available" line. Those belong
   to models, and this is not one; a "Coming soon" chip here would read as a
   ninth product we have not named, which is the one thing the honest-state rule
   in config/catalogue.ts exists to prevent.

   The handle is NOT typed here — it comes from content/ticker.ts, which already
   holds it and records that it was checked against the live account. Two copies
   of a social handle is how one of them ends up pointing at nobody. */
export const MORE_SPORTS = {
  label: "What's next",
  /* Rendered in the same display face and at the same size as a model's name,
     and held apart by COLOUR rather than by structure -- see MoreSports.module
     .css. Giving it a heading is what lets its body start on the same line as
     every neighbour's; a tile with no title began 67px higher than the cards
     beside it, which looked like a rendering fault rather than a quieter card. */
  heading: "More sports",
  /* Split so the handle can be interpolated from INSTAGRAM_HANDLE rather than
     retyped into the middle of a sentence. */
  ctaLead: "Follow",
  ctaTail: "on Instagram for updates",
} as const;

export const MODELS = {
  eyebrow: "The Models",
  heading: "Where each one's up to",
  /* No body line (Ned, 2026-08-06). It read "Six models across three codes, in
     two modes. Two are answering questions now and the rest are on the way." —
     a second count of the same grid it sits above, and the number it led with
     was how many models DON'T work yet. The cards say it themselves. */
} as const;

/* LEDGER removed 2026-08-03 (Ned). The section promised a published, closing-
   line-scored record of every call. We stay silent on the record instead —
   which also means not committing in marketing copy to a scoring mechanism
   nobody has built. Don't reinstate it as copy; reinstate it when there is a
   ledger to point at. */

export const PRICING_TEASER = {
  eyebrow: "Pricing",
  heading: "Pay for what you use",
  /* Don't quote a per-question credit cost (Ned, 2026-07-31). The allowance is
     the number that matters to someone choosing a plan; what a given question
     costs is shown in the product, before it runs. */
  body: "Every model on either plan. You see what a question costs before you ask it.",
  cta: "See pricing",
} as const;

/* The closing CTA, and what used to be here.

   Until 2026-08-28 this section was a mailing-list capture: an email box, a
   marketing consent, and "leave your email and we'll tell you when yours goes
   live". Ned's call to remove it — "let's just keep it to a free account" — and
   the launch is what made it removable rather than a loss.

   The form existed to announce a launch that has now happened. Both NBA models
   answer questions today and purchasing is live, so the only thing it could
   still offer was a message about something already true, in exchange for an
   address.

   It also carried the only marketing consent on the site with no account behind
   it. That is worth being explicit about, because deleting a consent clause
   looks like weakening one: WAITLIST.consent was written on 2026-08-27 to make
   an existing collection defensible under Spam Act s.16 — the address was
   already going onto the SES `marketing` topic as OPT_IN and the page said
   nothing. None of that reasoning is retracted. What changed is that there is
   no longer a collection here to justify. Consent is taken once now, in
   ConsentNotice, where it is versioned by TERMS_VERSION and recorded against a
   person with a server timestamp — a stronger record than an address on a list,
   and the reason this deletion is not a step backwards.

   ⚠️ The SES contact list still holds whoever joined while the form was up.
   Removing the form does not unsubscribe them and must not be read as having
   done so; they consented to the wording shown at the time and keep their
   unsubscribe link in every message.

   The free board is what makes "free account" an offer rather than a euphemism
   for a sign-up wall: it shows the next ~48h of games with a win probability on
   each, and the settled record beside it. Say that, not "join us". */
export const JOIN = {
  eyebrow: "Get Started",
  heading: "Start with a free account",
  /* Claims only what the board already keeps (Board.tsx: next ~48h, one win
     probability per game, every settled tip marked and counted). The
     calibration range is deliberately NOT offered here — it is the withheld
     thing the subscription sells, and the board's own copy says so. */
  body: "A free account opens the board: what the models make of the next couple of days, and how the last lot landed. Subscribe for the reasoning.",
  cta: "Create a free account",
  /* For someone who already has one. The nav carries Sign in as well, but this
     section is the page's last word and a returning reader should not have to
     scroll back up to act on it. */
  signInLead: "Already have one?",
  signIn: "Sign in",
} as const;

export const NAV = {
  models: "Models",
  board: "The Board",
  pricing: "Pricing",
  signIn: "Sign in",
  /* Short on purpose: it sits beside signIn in a 390px bar that fits its items
     only because none of them wraps. "Create a free account" does not fit. */
  createAccount: "Sign up",
  /* Deliberately the same length as signIn: the nav reserves one box for
     whichever of the two lands, so equal-length labels keep that box honest. */
  account: "Account",
  /* THE AUTHENTICATED BAR (Ned, 2026-09-07): "Fixtures · Insights · Fantasy ·
     Account" replaces "Models · The Board · Pricing · Account" once signed
     in — see Nav.tsx. The signed-out set above is unchanged; a visitor who
     has not signed up is still sold to, not shown a product nav. */
  fixtures: "Fixtures",
  insights: "Insights",
  /* WAS "Franco" / disabled "Soon" (kev-football-navigation-proposal.md,
     written before Franco's fantasy UI existed). It shipped onto this branch
     2026-09-07 (feature/franco-fantasy-ui, routes/Fantasy.tsx at
     `/app/fantasy/:sport?`), so the item is a working link now. The LABEL
     changes to "Fantasy" and not back to "Franco": the persona is still
     called Franco in body copy throughout Fantasy.tsx, but this nav bar
     names sections by what they ARE (Fixtures, Insights) rather than who
     narrates them, and "Fantasy" is what a reader is choosing between four
     codes' worth of. */
  fantasy: "Fantasy",
} as const;
